import { Menu } from '@headlessui/react';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import clsxm from '@/lib/clsxm';

import { chat, menuItem, menuPanel } from '@/components/Chat/chatUi';

/*
 * Building blocks of the menus on a document Marky wrote: the Download menu
 * and the "More" menu of a delivered document, and the "More" menu of a
 * Markdown file in Marky's Computer.
 */

export const DOCUMENT_MENU_WIDTH = 256;
const VIEWPORT_MARGIN = 8;

/** Sign in, then come back to this page. */
export function signInHref(): string {
  const back =
    typeof window === 'undefined'
      ? '/'
      : window.location.pathname + window.location.search;
  return `/login?redirectTo=${encodeURIComponent(back)}`;
}

export const DOCUMENT_MENU_PANEL_CLASS = clsxm(menuPanel, 'overflow-hidden');

/** A row of the menu; `active` is headlessui's keyboard/hover highlight. */
export function menuItemClass(active: boolean) {
  return clsxm(
    menuItem,
    'items-start gap-3 py-2',
    active &&
      'bg-[#f4f4fb] text-[#10101c] dark:bg-white/[0.06] dark:text-white',
  );
}

/** A divider between groups of rows. */
export function MenuDivider() {
  return (
    <div
      role='separator'
      className={clsxm('mx-2 my-1 border-t', chat.hairline)}
    />
  );
}

export function MenuItemIcon({ children }: { children: React.ReactNode }) {
  return (
    <span
      className={clsxm(
        'mt-px flex h-7 w-7 shrink-0 items-center justify-center rounded-md ring-1 ring-[#0f0f28]/[0.05] dark:ring-white/[0.06]',
        chat.soft,
      )}
    >
      {children}
    </span>
  );
}

export function MenuItemText({ label, hint }: { label: string; hint: string }) {
  return (
    <span className='min-w-0 flex-1'>
      <span className='block text-[13px] font-medium leading-5'>{label}</span>
      <span
        className={clsxm(
          'block truncate text-[12px] font-normal leading-4',
          chat.quiet,
        )}
        title={hint}
      >
        {/* Account emails are Latin: isolate them inside Arabic hints. */}
        <bdi>{hint}</bdi>
      </span>
    </span>
  );
}

export function MenuButtonItem({
  icon,
  label,
  hint,
  onSelect,
}: {
  icon: React.ReactNode;
  label: string;
  hint: string;
  onSelect: () => void;
}) {
  return (
    <Menu.Item>
      {({ active }) => (
        <button
          type='button'
          onClick={(e) => {
            e.stopPropagation();
            onSelect();
          }}
          className={menuItemClass(active)}
        >
          <MenuItemIcon>{icon}</MenuItemIcon>
          <MenuItemText label={label} hint={hint} />
        </button>
      )}
    </Menu.Item>
  );
}

/**
 * Positions the menu under its button (above when there's no room) in
 * document.body: attachment cards sit in overflow-hidden message containers
 * that would clip an inline dropdown, and the preview is itself a portal.
 */
export function MenuPortal({
  anchorRef,
  children,
}: {
  anchorRef: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const update = () => {
      const anchor = anchorRef.current;
      const panel = panelRef.current;
      if (!anchor || !panel) return;
      const r = anchor.getBoundingClientRect();
      const height = panel.offsetHeight;
      const width = panel.offsetWidth || DOCUMENT_MENU_WIDTH;
      const spaceBelow = window.innerHeight - r.bottom;
      const openUp = spaceBelow < height + 12 && r.top > spaceBelow;
      const top = openUp ? r.top - height - 6 : r.bottom + 6;
      // Under the anchor's end edge: its right edge, its left one in RTL.
      const rtl = getComputedStyle(anchor).direction === 'rtl';
      setPos({
        top: Math.max(
          VIEWPORT_MARGIN,
          Math.min(top, window.innerHeight - height - VIEWPORT_MARGIN),
        ),
        left: Math.min(
          Math.max(VIEWPORT_MARGIN, rtl ? r.left : r.right - width),
          window.innerWidth - width - VIEWPORT_MARGIN,
        ),
      });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [anchorRef]);

  if (typeof document === 'undefined') return null;
  return createPortal(
    <div
      ref={panelRef}
      style={{
        position: 'fixed',
        top: pos?.top ?? 0,
        left: pos?.left ?? 0,
        zIndex: 130,
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
